import { openChatCommand } from '../../ai-chat/lib/chatCommandHandoff.js';
import { commandRequest } from '../../../agents/context/ui-command-registry.js';
import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Select } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityNotesFeed } from '../../../components/entity-detail/EntityNotesFeed.js';
import type { EntityRelatedContext } from '../../../components/entity-detail/related-context.js';
import { useCurrentUser } from '../../../shared/scope/useCurrentScope.js';
import {
  useNotes,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
} from '../../notes/hooks/useNotes.js';
import { NoteFormModal } from '../../notes/components/NoteFormModal.js';
import { NoteShareModal } from '../../notes/components/NoteShareModal.js';
import { NoteVisibilityTag } from '../../notes/components/NoteVisibilityTag.js';
import type { NoteItem, NoteLinkInput } from '../../notes/api/notes.client.js';

export function ContactNotesTab({ contactId }: { contactId: string }): ReactElement {
  return <EntityNotesTab contactId={contactId} />;
}

export function EntityNotesTab({
  contactId,
  dealId,
  companyId,
  taskId,
}: EntityRelatedContext): ReactElement {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const relation = taskId
    ? { taskId }
    : dealId
      ? { dealId }
      : companyId
        ? { companyId }
        : { contactId: contactId! };
  const query = useNotes(relation);
  const create = useCreateNoteMutation();
  const update = useUpdateNoteMutation();
  const remove = useDeleteNoteMutation();
  const [editing, setEditing] = useState<NoteItem | null>(null);
  const [sharing, setSharing] = useState<NoteItem | null>(null);
  const [attach, setAttach] = useState(false);
  const [search, setSearch] = useState('');
  const all = useNotes(search.trim() ? { q: search.trim() } : {}, { enabled: attach });
  const prefix = 'crm.features.features.notes.components.entitynotespanel.';
  const attachNote = (id: string): void => {
    const note = all.data?.find((n) => n.id === id);
    if (!note) return;
    const links: NoteLinkInput[] = note.links.map((link) => ({
      ...(link.dealId ? { dealId: link.dealId } : {}),
      ...(link.companyId ? { companyId: link.companyId } : {}),
      ...(link.contactId ? { contactId: link.contactId } : {}),
      ...(link.taskId ? { taskId: link.taskId } : {}),
      ...(link.calendarEventId ? { calendarEventId: link.calendarEventId } : {}),
      ...(link.documentId ? { documentId: link.documentId } : {}),
      ...(link.templateId ? { templateId: link.templateId } : {}),
      ...(link.projectId ? { projectId: link.projectId } : {}),
    }));
    update.mutate(
      { id, input: { links: [...links, relation] } },
      {
        onSuccess: () => {
          setAttach(false);
          setSearch('');
        },
      },
    );
  };
  return (
    <>
      <EntityNotesFeed
        loading={query.isLoading}
        saving={create.isPending}
        error={query.error ?? update.error ?? remove.error}
        onSave={(body) => create.mutateAsync({ body, links: [relation] })}
        extra={
          <Button type="link" onClick={() => setAttach(!attach)}>
            {t(`${prefix}${attach ? 'не_прикреплять' : 'прикрепить_существующую'}`)}
          </Button>
        }
        notes={(query.data ?? []).map((note) => ({
          id: note.id,
          body: note.body,
          createdAt: note.createdAt,
          author: note.createdByMember?.user.name || t(`${prefix}неизвестный_автор`),
          badge: <NoteVisibilityTag visibility={note.visibility} />,
          visibleActions: (
            <Button
              type="text"
              size="small"
              onClick={() =>
                openChatCommand(
                  navigate,
                  commandRequest('extractTasksFromNote', { noteId: note.id }),
                )
              }
            >
              {t(`${prefix}извлечь_задачи`)}
            </Button>
          ),
          actions: [
            { key: 'edit', label: t('crm.entity_detail.edit'), onClick: () => setEditing(note) },
            ...(note.createdByMember?.userId === user?.id
              ? [{ key: 'share', label: t(`${prefix}поделиться`), onClick: () => setSharing(note) }]
              : []),
            {
              key: 'delete',
              label: t('crm.entity_detail.delete_note'),
              danger: true,
              disabled: remove.isPending,
              confirm: {
                title: t('crm.entity_detail.delete_confirm'),
                description: t(`${prefix}заметка_пропадет_из_списка_и_со_всех_карто`),
              },
              onClick: () => remove.mutateAsync(note.id),
            },
          ],
        }))}
      />
      {attach ? (
        <Select
          showSearch
          filterOption={false}
          style={{ width: '100%' }}
          aria-label={t(`${prefix}найти_заметку_по_тексту`)}
          placeholder={t(`${prefix}найти_заметку_по_тексту`)}
          onSearch={setSearch}
          loading={all.isLoading || update.isPending}
          value={null}
          onChange={attachNote}
          options={(all.data ?? [])
            .filter((n) => !query.data?.some((linked) => linked.id === n.id))
            .map((n) => ({ value: n.id, label: n.body.slice(0, 80) }))}
        />
      ) : null}
      {editing ? <NoteFormModal open note={editing} onClose={() => setEditing(null)} /> : null}
      {sharing ? <NoteShareModal open note={sharing} onClose={() => setSharing(null)} /> : null}
    </>
  );
}
